import { useJobillaOrgId } from '@/hooks'
import { logger } from '@/ui'
import type { EmailTemplateData } from '@jobilla/nexus-sdk'
import { useEmailTemplatesIndex } from '@jobilla/nexus-sdk'
import { getRouteApi, Link } from '@tanstack/react-router'
import { FileTextIcon, SearchIcon, SettingsIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const companyRoute = getRouteApi('/_auth/company/$companyId')

export interface TemplatePickerProps {
	onSelect: (template: { subject: string | null; body: string }) => void
}

export function TemplatePicker({ onSelect }: TemplatePickerProps) {
	const organisationId = useJobillaOrgId()
	const { companyId } = companyRoute.useParams()

	const [isOpen, setIsOpen] = useState(false)
	const [search, setSearch] = useState('')
	const dropdownRef = useRef<HTMLDivElement>(null)
	const searchInputRef = useRef<HTMLInputElement>(null)

	const { data: templatesResponse } = useEmailTemplatesIndex(
		{ 'filter[organisation_id]': organisationId, sort: ['name'] },
		{ enabled: !!organisationId && isOpen },
	)

	const templates = useMemo(() => templatesResponse ?? [], [templatesResponse])

	const filtered = useMemo(() => {
		if (!search.trim()) { return templates }
		const q = search.toLowerCase()
		return templates.filter(
			(t) => t.name.toLowerCase().includes(q) || t.subject?.toLowerCase().includes(q),
		)
	}, [templates, search])

	const handleSelect = useCallback(
		(template: EmailTemplateData) => {
			logger.track('Email template: Selected', {
				data: {
					template_id: template.id,
					template_name: template.name,
				},
			})
			onSelect({ subject: template.subject, body: template.body })
			setIsOpen(false)
			setSearch('')
		},
		[onSelect],
	)

	useEffect(() => {
		if (isOpen && searchInputRef.current) {
			searchInputRef.current.focus()
		}
	}, [isOpen])

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
				setIsOpen(false)
				setSearch('')
			}
		}
		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
			return () => document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	return (
		<div ref={dropdownRef} className="relative">
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-md transition-colors"
			>
				<FileTextIcon className="size-3.5" />
				Use template
			</button>

			{isOpen && (
				<div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 w-72">
					<div className="p-2 border-b border-gray-100">
						<div className="relative">
							<SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-gray-400" />
							<input
								ref={searchInputRef}
								type="text"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder="Search templates..."
								className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
							/>
						</div>
					</div>

					<div className="max-h-60 overflow-y-auto">
						{filtered.length === 0
							? (
								<div className="px-3 py-4 text-sm text-gray-400 text-center">
									{templates.length === 0 ? 'No templates yet' : 'No matches'}
								</div>
							)
							: filtered.map((template) => (
								<button
									key={template.id}
									type="button"
									onClick={() => handleSelect(template)}
									className="w-full text-left px-3 py-2.5 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
								>
									<div className="text-sm font-medium text-gray-700 truncate">{template.name}</div>
									{template.subject && (
										<div className="text-xs text-gray-400 truncate mt-0.5">{template.subject}</div>
									)}
								</button>
							))}
					</div>

					{companyId && (
						<div className="border-t border-gray-100 p-2">
							<Link
								to="/company/$companyId/settings/email-templates"
								params={{ companyId }}
								className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
								onClick={() => setIsOpen(false)}
							>
								<SettingsIcon className="size-3.5" />
								Manage templates
							</Link>
						</div>
					)}
				</div>
			)}
		</div>
	)
}
